import { Button, Menu, MenuButton, MenuItem, MenuList } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  TimeTrackingEffectivenessReportExportDocument,
  type TimeTrackingEffectivenessReportExportQuery,
  type TimeTrackingEffectivenessReportExportQueryVariables,
  TimeTrackingReportExportDocument,
  TimeTrackingReportExportPdfDocument,
  type TimeTrackingReportExportPdfQuery,
  type TimeTrackingReportExportPdfQueryVariables,
  type TimeTrackingReportExportQuery,
  type TimeTrackingReportExportQueryVariables,
  TimeTrackingReportExportType,
  TimeTrackingReportsWhereColumn,
  TimeTrackingTimeOffsExportDocument,
  type TimeTrackingTimeOffsExportQuery,
  type TimeTrackingTimeOffsExportQueryVariables,
} from "@src/__generated__/graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import type { Filters } from "@src/utils/components/filters/models";
import { useStore } from "@src/utils/hooks";
import { computed } from "mobx";
import { observer, useLocalObservable } from "mobx-react-lite";

export interface ExportBtnProps {
  where: Filters<TimeTrackingReportsWhereColumn>;
}

const ExportBtn = ({ where }: ExportBtnProps) => {
  const { UIStore } = useStore();
  const {
    exportSummary,
    exportDetailed,
    fetchReportsExport,
    handleUserEffectivenessExport,
    handleTimeOffExport,
    isLoading,
  } = useLocalObservable(
    () => ({
      reportFetcher: new FetchHelper<
        TimeTrackingReportExportQuery,
        TimeTrackingReportExportQueryVariables
      >(TimeTrackingReportExportDocument),
      effectivenessFetcher: new FetchHelper<
        TimeTrackingEffectivenessReportExportQuery,
        TimeTrackingEffectivenessReportExportQueryVariables
      >(TimeTrackingEffectivenessReportExportDocument),
      timeOffsFetcher: new FetchHelper<
        TimeTrackingTimeOffsExportQuery,
        TimeTrackingTimeOffsExportQueryVariables
      >(TimeTrackingTimeOffsExportDocument),
      pdfFetcher: new FetchHelper<
        TimeTrackingReportExportPdfQuery,
        TimeTrackingReportExportPdfQueryVariables
      >(TimeTrackingReportExportPdfDocument),

      async fetchReportsExport() {
        const [data, error] = await this.reportFetcher.fetch({
          where: this.where,
        });

        if (error || !data?.timeTrackingReportExport) return;
        window.location.href = data.timeTrackingReportExport;
      },

      async handleUserEffectivenessExport() {
        const [data, error] = await this.effectivenessFetcher.fetch({
          where: this.where,
        });

        if (error || !data?.userEffectivenessReportExport) return;
        window.location.href = data.userEffectivenessReportExport;
      },

      async exportSummary() {
        if (!this.dateRangeFilterValue) {
          UIStore.toast({
            status: "error",
            title: t`Please select a date range.`,
          });
          captureEvent({
            message: t`FE: Missing params for timeTrackingReportExportPdf function.`,
          });
          return;
        }

        const [data, error] = await this.pdfFetcher.fetch({
          from: this.dateRangeFilterValue[0],
          to: this.dateRangeFilterValue[1],
          where: this.where,
          type: TimeTrackingReportExportType.Summary,
        });

        if (error || !data?.timeTrackingReportExportPdf) {
          UIStore.toast({
            status: "error",
          });
          return;
        }

        window.open(data.timeTrackingReportExportPdf, "_blank");
      },

      async exportDetailed() {
        if (!this.dateRangeFilterValue) {
          UIStore.toast({
            status: "error",
            title: t`Please select a date range.`,
          });
          captureEvent({
            message: t`FE: Missing params for timeTrackingReportExportPdf function.`,
          });
          return;
        }

        const [data, error] = await this.pdfFetcher.fetch({
          from: this.dateRangeFilterValue[0],
          to: this.dateRangeFilterValue[1],
          where: this.where,
          type: TimeTrackingReportExportType.Detailed,
        });

        if (error || !data?.timeTrackingReportExportPdf) {
          UIStore.toast({
            status: "error",
          });
          return;
        }

        window.open(data.timeTrackingReportExportPdf, "_blank");
      },

      async handleTimeOffExport() {
        if (!this.dateRangeFilterValue) {
          UIStore.toast({
            status: "error",
            title: t`Something went wrong, please try again.`,
          });
          captureEvent({
            message: t`FE: Missing params for timeOffsExport function.`,
          });
          return;
        }

        const [data, error] = await this.timeOffsFetcher.fetch({
          from: this.dateRangeFilterValue[0],
          to: this.dateRangeFilterValue[1],
        });

        if (error || !data?.timeOffsExport) {
          UIStore.toast({
            status: "error",
            title: t`Something went wrong, please try again.`,
          });
          return;
        }

        window.open(data.timeOffsExport, "_blank");
      },

      get isLoading() {
        return (
          this.reportFetcher.isLoading.value ||
          this.effectivenessFetcher.isLoading.value ||
          this.timeOffsFetcher.isLoading.value ||
          this.pdfFetcher.isLoading.value
        );
      },

      get dateRangeFilterValue() {
        const values = where.filtersByColumn.get(
          TimeTrackingReportsWhereColumn.TrackedForDate,
        )?.value;

        if (!values || values.length !== 2) return null;

        return values;
      },

      get where() {
        return where.asWhereParam;
      },
    }),
    {
      isLoading: computed,
      dateRangeFilterValue: computed,
      where: computed,
    },
  );

  return (
    <Menu>
      <MenuButton
        as={Button}
        colorScheme="grey"
        isLoading={isLoading}
        leftIcon={<Icon name="download-02" />}
        rightIcon={<Icon name="chevron-down" />}
        variant="outline"
      >
        <Trans>Export</Trans>
      </MenuButton>

      <MenuList>
        <MenuItem onClick={fetchReportsExport}>
          <Trans>Time report</Trans>
        </MenuItem>
        <MenuItem onClick={handleUserEffectivenessExport}>
          <Trans>User effectiveness</Trans>
        </MenuItem>
        <MenuItem onClick={handleTimeOffExport}>
          <Trans>Time-offs</Trans>
        </MenuItem>
        <MenuItem onClick={exportSummary}>
          <Trans>Summary Time report PDF</Trans>
        </MenuItem>
        <MenuItem onClick={exportDetailed}>
          <Trans>Detailed Time report PDF</Trans>
        </MenuItem>
      </MenuList>
    </Menu>
  );
};

export default observer(ExportBtn);
